import classNames from "classnames";
import { Immutable } from "immer";
import React from "react";
import SVG from "react-inlinesvg";
import { t } from "ttag";

import iconQuestionMarkCircleFilled from "../../../../img/icons/question-mark-circle-filled.svg";
import { Flair } from "../../../components/Flair";
import { Image } from "../../../components/Image";
import { BundleGroupTypes, ConfiguratorTypes } from "../../../constants";
import { IImageURL, IProductID } from "../../../models/nominals";
import { IProductFlair } from "../../../models/product-compare";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import {
    trackConfigUpsellAddedEvent,
    trackConfigUpsellRemovedEvent,
} from "../../../utils/analytics";
import { PromoCardInfoModal } from "../ConfiguratorPromoCardInfoModal";
import { useUpsellAnalytics } from "../hooks/useUpsellAnalytics";
import { IModularConfiguratorPromoCardInfoModal } from "../models";
import { showUpsellInfoModal, updateSelectedAddons } from "../reducers";
import { getUpsellInfoModal } from "../selectors";

import styles from "./index.module.scss";

const OUT_OF_STOCK_FLAIR_STYLE = "flair--out-of-stock";

const getAnalyticsClassName = (bundleType: string): string => {
    const analyticsClassMap: { [key: string]: string } = {
        [BundleGroupTypes.PROMO_CARD]: "al-promo-GWP-card",
        [BundleGroupTypes.IN_CONFIGURATOR_ADD_ON]: "al-add-on-card",
        [BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT]: "al-free-gift",
        [BundleGroupTypes.UPGRADE_MODEL]: "al-change-model-upgrade",
        [BundleGroupTypes.UPGRADE_PRODUCT]: "al-standard-upgrade-card",
    };
    return analyticsClassMap[bundleType];
};

export const ConfiguratorUpsell = (
    props: Immutable<{
        bundleType: string;
        bundleID: number | null;
        sectionTitle: string | null;
        lineOne: string | null;
        lineTwo: string | null;
        lineThree: string | null;
        lineThreeStrikethrough?: string | null;
        cardImage?: IImageURL | null;
        flair?: IProductFlair | null;
        isSelected: boolean;
        isUnavailable?: boolean;
        unavailableMessage?: string | null;
        productUpgradeID?: IProductID;
        infoModal?: IModularConfiguratorPromoCardInfoModal | null;
        configuratorType?: ConfiguratorTypes;
    }>,
): React.ReactNode => {
    const [isInfoModalOpen, setIsInfoModalOpen] = React.useState(false);

    const dispatch = useAppDispatch();

    const upsellInfoModal = useAppSelector((rootState) =>
        getUpsellInfoModal(rootState.configurator, {
            bundleID: props.bundleID,
        }),
    );
    const hasUpsellInfoModalContent = upsellInfoModal !== null;
    const modalContent = upsellInfoModal;

    const isFreeGift =
        props.bundleType === BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT;
    const isPromoCard = props.bundleType === BundleGroupTypes.PROMO_CARD;
    const configName = props.lineOne || props.sectionTitle || "";

    const { selectUpgrade, clearUpgrade } = useUpsellAnalytics(configName, {
        shownEvent: isFreeGift || isPromoCard ? "no-checkbox" : "default",
    });

    const onSelectChange = (e: React.FormEvent<HTMLInputElement>) => {
        // `disabled` on the input below is the real enforcement — browsers
        // never fire `change` on a disabled input. jsdom's fireEvent.click
        // does, so this guard is also what the unselectable-when-OOS test
        // exercises.
        if (props.isUnavailable) {
            return;
        }
        if (props.productUpgradeID) {
            if (
                props.bundleType === BundleGroupTypes.UPGRADE_PRODUCT ||
                props.bundleType === BundleGroupTypes.UPGRADE_MODEL
            ) {
                if (props.isSelected) {
                    clearUpgrade();
                } else {
                    selectUpgrade(props.productUpgradeID);
                }
            } else {
                const updates = [
                    {
                        productID: props.productUpgradeID,
                        quantity: e.currentTarget.checked ? 1 : 0,
                    },
                ];
                dispatch(updateSelectedAddons(updates));
                if (e.currentTarget.checked) {
                    trackConfigUpsellAddedEvent(configName);
                } else {
                    trackConfigUpsellRemovedEvent(configName);
                }
            }
        }
    };

    const cardAnalyticsClass = getAnalyticsClassName(props.bundleType);
    const cardClasses = classNames({
        [styles.card]: true,
        [styles.selectedCard]: props.isSelected && !isFreeGift,
        [styles.freeGiftCard]: isFreeGift,
        [styles.promoCard]: isPromoCard,
        [styles.unavailableCard]: props.isUnavailable,
        [cardAnalyticsClass]: true,
        // Apply border to Upsell due to its flair style
        // Add other styles if needed
        [styles.flairSealyLimeYellow]:
            props.flair?.style === "flair--sealy-lime-yellow",
    });
    const tooltipAnalyticsClass = `${cardAnalyticsClass}-tool-tip`;

    const stockFlair: IProductFlair | null =
        props.isUnavailable && props.unavailableMessage
            ? {
                  content: props.unavailableMessage,
                  style: OUT_OF_STOCK_FLAIR_STYLE,
              }
            : null;

    // Stock status replaces the editorial flairs rather than stacking with
    // them — they would otherwise compete for the same corner of the card.
    const cardFlairs = stockFlair
        ? [stockFlair]
        : [
              // Passed in as a prop...
              props.flair,
              // ...or carried by the corresponding Upsell Info Modal.
              modalContent?.card_flair?.[0]?.value,
          ].filter((flair) => !!flair);

    return (
        <div className={styles.container}>
            <h2
                className={`${styles.sectionTitle} configurator-upsell__section-title`}
            >
                {props.sectionTitle}
            </h2>
            <label className={cardClasses}>
                {cardFlairs.map((flair, idx) => (
                    <Flair key={idx} flair={flair} inline={true} />
                ))}
                <div>
                    {props.productUpgradeID && !isFreeGift && (
                        <input
                            className={styles.checkbox}
                            type="checkbox"
                            checked={props.isSelected}
                            onChange={onSelectChange}
                            disabled={props.isUnavailable}
                        />
                    )}
                    <span className={styles.lineOne}>{props.lineOne}</span>
                </div>
                <p>
                    <span className={styles.lineTwo}>{props.lineTwo}</span>
                    <span className={styles.lineThree}>
                        {props.lineThree}
                        {props.lineThreeStrikethrough && (
                            <span className={styles.lineThreeStrikethrough}>
                                {props.lineThreeStrikethrough}
                            </span>
                        )}
                    </span>
                </p>
                {props.cardImage && props.lineTwo && (
                    <Image
                        className={styles.cardImage}
                        src={props.cardImage}
                        alt={props.lineTwo}
                    />
                )}
            </label>
            {hasUpsellInfoModalContent && props.bundleID !== null && (
                <button
                    className={`${styles.modalTrigger} ${tooltipAnalyticsClass}`}
                    onClick={() => {
                        if (props.bundleID !== null) {
                            dispatch(
                                showUpsellInfoModal({
                                    bundleID: props.bundleID,
                                }),
                            );
                        }
                    }}
                    // TODO(i18n): Issue #12 - Use complete sentence with interpolation instead of template literal interpolation for proper translation: t`More information on ${props.sectionTitle}`
                    aria-label={t`More Information on the ${props.sectionTitle}`}
                >
                    <SVG src={iconQuestionMarkCircleFilled} />
                </button>
            )}
            {props.infoModal && props.configuratorType && (
                <>
                    <button
                        className={`${styles.modalTrigger} ${tooltipAnalyticsClass}`}
                        onClick={() => {
                            setIsInfoModalOpen(true);
                        }}
                        // TODO(i18n): Issue #13 - Use complete sentence with interpolation instead of template literal interpolation for proper translation: t`More information on ${props.sectionTitle}`
                        aria-label={t`More Information on the ${props.sectionTitle}`}
                    >
                        <SVG src={iconQuestionMarkCircleFilled} />
                    </button>
                    <PromoCardInfoModal
                        configuratorType={props.configuratorType}
                        infoModal={props.infoModal}
                        isOpen={isInfoModalOpen}
                        onCloseModal={() => {
                            setIsInfoModalOpen(false);
                        }}
                        modalTitle={props.sectionTitle}
                        title={props.lineTwo}
                        cardImage={props.cardImage}
                    />
                </>
            )}
        </div>
    );
};
